/*
 * Marketim home sections (owner: agent "home").
 * Vanilla custom elements: <mk-band>, <mk-slider>, <mk-bundle>.
 * No CSS transform animations on wide layers: the band scrolls via scrollLeft.
 */

(function () {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ */
  /* <mk-hero-slider>: opacity crossfade, auto-advance, dots, swipe       */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('mk-hero-slider')) {
    class MkHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-mk-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-mk-dot]'));
        this.prevButton = this.querySelector('[data-mk-prev]');
        this.nextButton = this.querySelector('[data-mk-next]');
        this.live = this.querySelector('[data-mk-slides]');
        if (this.slides.length < 2) return;

        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.interval = Number(this.dataset.interval) || 6000;
        this.elapsed = 0;
        this.hovering = false;
        this.focused = false;
        this.touching = false;
        this.inView = true;

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(this.index - 1, true));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hovering = true;
        });
        this.addEventListener('pointerleave', () => {
          this.hovering = false;
        });
        this.addEventListener('focusin', () => {
          this.focused = true;
        });
        this.addEventListener('focusout', () => {
          this.focused = false;
        });

        let startX = 0;
        let startY = 0;
        this.addEventListener(
          'touchstart',
          (event) => {
            this.touching = true;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );
        this.addEventListener(
          'touchend',
          (event) => {
            this.touching = false;
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1), true);
          },
          { passive: true }
        );
        this.addEventListener('touchcancel', () => {
          this.touching = false;
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
          });
          this.observer.observe(this);
        }

        this.onMotionChange = () => this.updateState();
        reducedMotionQuery.addEventListener('change', this.onMotionChange);

        this.timer = setInterval(() => this.tick(), 200);
        this.updateState();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        if (this.observer) this.observer.disconnect();
        if (this.onMotionChange) reducedMotionQuery.removeEventListener('change', this.onMotionChange);
      }

      get autoplay() {
        return !reducedMotionQuery.matches;
      }

      tick() {
        if (!this.autoplay || this.hovering || this.focused || this.touching) return;
        if (!this.inView || document.hidden) return;
        this.elapsed += 200;
        if (this.elapsed >= this.interval) this.go(this.index + 1, false);
      }

      go(next, manual) {
        const count = this.slides.length;
        const target = (next + count) % count;
        this.elapsed = 0;
        if (target === this.index) return;
        this.slides[this.index].classList.remove('is-active');
        this.slides[this.index].setAttribute('aria-hidden', 'true');
        this.slides[target].classList.add('is-active');
        this.slides[target].removeAttribute('aria-hidden');
        if (this.dots[this.index]) {
          this.dots[this.index].classList.remove('is-active');
          this.dots[this.index].removeAttribute('aria-current');
        }
        if (this.dots[target]) {
          this.dots[target].classList.add('is-active');
          this.dots[target].setAttribute('aria-current', 'true');
        }
        this.index = target;
        if (manual) this.elapsed = 0;
      }

      updateState() {
        const playing = this.autoplay;
        if (this.live) this.live.setAttribute('aria-live', playing ? 'off' : 'polite');
      }
    }

    customElements.define('mk-hero-slider', MkHeroSlider);
  }

  /* ------------------------------------------------------------------ */
  /* <mk-band>: static wrapped row on mobile, scrollLeft marquee on desktop */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('mk-band')) {
    class MkBand extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-mk-track]');
        this.pauseButton = this.querySelector('[data-mk-pause]');
        if (!this.track) return;

        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.speed = Number(this.dataset.speed) || 50;
        this.position = 0;
        this.userPaused = false;
        this.hovering = false;
        this.inView = true;
        this.rafId = null;
        this.lastTime = 0;
        this.originals = Array.from(this.track.children);

        this.onChange = () => this.setup();
        this.desktopQuery.addEventListener('change', this.onChange);
        reducedMotionQuery.addEventListener('change', this.onChange);

        this.onEnter = () => {
          this.hovering = true;
        };
        this.onLeave = () => {
          this.hovering = false;
        };
        this.track.addEventListener('pointerenter', this.onEnter);
        this.track.addEventListener('pointerleave', this.onLeave);

        if (this.pauseButton) {
          this.pauseButton.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            this.updatePauseButton();
          });
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
          });
          this.observer.observe(this);
        }

        this.setup();
      }

      disconnectedCallback() {
        this.stop();
        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onChange);
        reducedMotionQuery.removeEventListener('change', this.onChange);
        if (this.observer) this.observer.disconnect();
      }

      removeClones() {
        this.track.querySelectorAll('[data-mk-clone]').forEach((node) => node.remove());
      }

      setup() {
        this.stop();
        this.removeClones();
        this.track.scrollLeft = 0;
        this.position = 0;
        this.removeAttribute('data-ready');
        this.removeAttribute('data-static');
        if (this.pauseButton) this.pauseButton.hidden = true;

        if (!this.desktopQuery.matches) return;

        if (reducedMotionQuery.matches || this.originals.length === 0) {
          this.setAttribute('data-static', '');
          return;
        }

        // Duplicate the set until it is wider than the viewport twice over, so the loop is seamless.
        const firstWidth = this.setWidth();
        if (firstWidth <= 0) return;
        const copies = Math.max(1, Math.ceil(this.track.clientWidth / firstWidth)) + 1;
        for (let i = 0; i < copies; i++) {
          this.originals.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-mk-clone', '');
            this.track.appendChild(clone);
          });
        }

        this.setAttribute('data-ready', '');
        if (this.pauseButton) {
          this.pauseButton.hidden = false;
          this.updatePauseButton();
        }
        this.start();
      }

      // Width of one full set of original items, including the flex gap.
      setWidth() {
        const first = this.originals[0];
        const last = this.originals[this.originals.length - 1];
        if (!first || !last) return 0;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
      }

      updatePauseButton() {
        if (!this.pauseButton) return;
        const label = this.pauseButton.querySelector('[data-mk-pause-label]');
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        if (label) label.textContent = this.userPaused ? 'Resume scrolling text' : 'Pause scrolling text';
      }

      start() {
        this.lastTime = 0;
        const step = (time) => {
          if (!this.lastTime) this.lastTime = time;
          const delta = Math.min(time - this.lastTime, 100) / 1000;
          this.lastTime = time;

          if (!this.userPaused && !this.hovering && this.inView && !document.hidden) {
            const loopWidth = this.setWidth();
            this.position += this.speed * delta;
            if (loopWidth > 0 && this.position >= loopWidth) this.position -= loopWidth;
            this.track.scrollLeft = this.position;
          }
          this.rafId = requestAnimationFrame(step);
        };
        this.rafId = requestAnimationFrame(step);
      }

      stop() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    customElements.define('mk-band', MkBand);
  }

  /* ------------------------------------------------------------------ */
  /* <mk-slider>: scroll-snap row with prev/next buttons                 */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('mk-slider')) {
    class MkSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-mk-track]');
        this.prev = this.querySelector('[data-mk-prev]');
        this.next = this.querySelector('[data-mk-next]');
        this.nav = this.querySelector('.mk-slider__nav');
        if (!this.track) return;

        this.ticking = false;
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
        if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

        this.track.addEventListener('keydown', (event) => {
          if (event.target !== this.track) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.scrollByPage(1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.scrollByPage(-1);
          }
        });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.update());
          this.resizeObserver.observe(this.track);
        }
        this.update();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      scrollByPage(direction) {
        const amount = this.track.clientWidth * 0.85 * direction;
        this.track.scrollBy({ left: amount, behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const left = Math.abs(this.track.scrollLeft);
        if (this.nav) this.nav.hidden = max <= 2;
        if (this.prev) this.prev.disabled = left <= 2;
        if (this.next) this.next.disabled = left >= max - 2;
      }
    }

    customElements.define('mk-slider', MkSlider);
  }

  /* ------------------------------------------------------------------ */
  /* <mk-bundle>: add one / add all via a single /cart/add.js call        */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('mk-bundle')) {
    class MkBundle extends HTMLElement {
      connectedCallback() {
        this.status = this.querySelector('[data-mk-status]');
        this.allButton = this.querySelector('[data-mk-add-all]');
        this.busy = false;

        this.addEventListener('click', (event) => {
          const one = event.target.closest('[data-mk-add-one]');
          if (one && this.contains(one)) {
            const item = one.closest('[data-mk-item]');
            if (item) this.add([item], item);
            return;
          }
          const all = event.target.closest('[data-mk-add-all]');
          if (all && this.contains(all)) {
            this.add(Array.from(this.querySelectorAll('[data-mk-item]')), null);
          }
        });
      }

      setStatus(text, state) {
        if (!this.status) return;
        this.status.textContent = text || '';
        if (state) this.status.dataset.state = state;
        else delete this.status.dataset.state;
      }

      async add(items, singleItem) {
        const variantIds = items.map((el) => Number(el.dataset.variantId)).filter(Boolean);
        if (this.busy || variantIds.length === 0) return;
        this.busy = true;
        this.setStatus('', null);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const busyEls = singleItem ? [singleItem] : items;
        busyEls.forEach((el) => el.classList.add('is-busy'));
        if (!singleItem && this.allButton) this.allButton.setAttribute('aria-busy', 'true');

        const body = { items: variantIds.map((id) => ({ id, quantity: 1 })) };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        try {
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            this.setStatus(data.description || data.message || this.dataset.errorText, 'error');
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          // Dawn's cart elements expect a single-line shape (id/key) plus rendered sections.
          const first = Array.isArray(data.items) && data.items.length ? data.items[0] : {};
          const state = Object.assign({}, first, data);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'mk-bundle',
              productVariantId: first.variant_id || variantIds[0],
              cartData: state,
            });
          }
          cart.renderContents(state);
          this.setStatus(this.dataset.addedText || '', 'ok');
        } catch (error) {
          console.error(error);
          this.setStatus(this.dataset.errorText || '', 'error');
        } finally {
          busyEls.forEach((el) => el.classList.remove('is-busy'));
          if (this.allButton) this.allButton.removeAttribute('aria-busy');
          this.busy = false;
        }
      }
    }

    customElements.define('mk-bundle', MkBundle);
  }
})();
